import { TaskGroupBy, TaskStatus } from "@common/types/task";
import { ORGANIZATION_DEFAULT_TASK_STATUSES } from "./taskListConstants";

export interface TaskListCapabilities {
    canCreateTasks: boolean;
    canSelectRows: boolean;
    canFilterByAssignee: boolean;
    canFilterByClientCreator: boolean;
    canFilterByClientAssignee: boolean;
    canFilterByClient: boolean;
    canFilterByEntity: boolean;
    canGroupByTaskGroup: boolean;
    canGroupByClient: boolean;
    isCrossWorkspace: boolean;
    /** Unassigned, Waiting on client and Client requests built-in views. */
    hasClientTaskViews: boolean;
    /** The workspace's firm membership is loading, so `hasClientTaskViews` may still turn on. */
    isLoadingClientTaskViews: boolean;
    defaultStatuses: TaskStatus[] | undefined;
    /** Board only: how many cards a selection holds; one keeps bulk updates off. */
    boardSelection: "multiple" | "single";
}

export const WORKSPACE_TASK_LIST_CAPABILITIES: TaskListCapabilities = {
    canCreateTasks: true,
    canSelectRows: true,
    canFilterByAssignee: true,
    canFilterByClientCreator: false,
    canFilterByClientAssignee: false,
    canFilterByClient: false,
    canFilterByEntity: true,
    canGroupByTaskGroup: true,
    canGroupByClient: false,
    isCrossWorkspace: false,
    hasClientTaskViews: false,
    isLoadingClientTaskViews: false,
    defaultStatuses: undefined,
    boardSelection: "multiple",
};

const ORGANIZATION_MEMBER_WORKSPACE_TASK_LIST_CAPABILITIES: TaskListCapabilities =
    {
        ...WORKSPACE_TASK_LIST_CAPABILITIES,
        canFilterByClientCreator: true,
        canFilterByClientAssignee: true,
        hasClientTaskViews: true,
    };

const LOADING_MEMBERSHIP_WORKSPACE_TASK_LIST_CAPABILITIES: TaskListCapabilities =
    {
        ...WORKSPACE_TASK_LIST_CAPABILITIES,
        isLoadingClientTaskViews: true,
    };

const getLoadedWorkspaceTaskListCapabilities = (
    isOrganizationMember: boolean,
): TaskListCapabilities =>
    isOrganizationMember
        ? ORGANIZATION_MEMBER_WORKSPACE_TASK_LIST_CAPABILITIES
        : WORKSPACE_TASK_LIST_CAPABILITIES;

/** The workspace page's capabilities: firm members in a firm-managed workspace can also filter by client assignee and creator, and get the client views. */
export const getWorkspaceTaskListCapabilities = ({
    isOrganizationMember,
    isLoadingMembership,
}: {
    isOrganizationMember: boolean;
    isLoadingMembership: boolean;
}): TaskListCapabilities =>
    isLoadingMembership
        ? LOADING_MEMBERSHIP_WORKSPACE_TASK_LIST_CAPABILITIES
        : getLoadedWorkspaceTaskListCapabilities(isOrganizationMember);

export const ORGANIZATION_TASK_LIST_CAPABILITIES: TaskListCapabilities = {
    canCreateTasks: false,
    canSelectRows: true,
    canFilterByAssignee: true,
    canFilterByClientCreator: true,
    canFilterByClientAssignee: true,
    canFilterByClient: true,
    canFilterByEntity: false,
    canGroupByTaskGroup: false,
    canGroupByClient: true,
    isCrossWorkspace: true,
    hasClientTaskViews: true,
    isLoadingClientTaskViews: false,
    defaultStatuses: ORGANIZATION_DEFAULT_TASK_STATUSES,
    boardSelection: "single",
};

export const isTaskGroupBySupported = ({
    groupBy,
    capabilities,
}: {
    groupBy: TaskGroupBy | undefined;
    capabilities: TaskListCapabilities;
}): boolean => groupBy !== TaskGroupBy.CLIENT || capabilities.canGroupByClient;

/**
 * The grouping List's groups and Board's columns show for `groupBy`: the
 * requested one when the page supports it, else Status.
 */
export const getTaskListGroupBy = ({
    groupBy,
    capabilities,
}: {
    groupBy: TaskGroupBy | undefined;
    capabilities: TaskListCapabilities;
}): TaskGroupBy =>
    groupBy !== undefined && isTaskGroupBySupported({ groupBy, capabilities })
        ? groupBy
        : TaskGroupBy.STATUS;

/** The page's scope, which decides which groupings it can ever offer. */
export interface TaskGroupingScope {
    capabilities: TaskListCapabilities;
    hasOrganization: boolean;
}

/** What decides which groupings a Tasks page offers right now. */
export interface TaskGroupingContext extends TaskGroupingScope {
    hasCloseStages: boolean;
    hasCloses: boolean;
}

/**
 * Whether the page's scope allows `groupBy`, whatever has loaded: stage and
 * service need an organization, task group and client the page's capability.
 */
export const isTaskGroupByInScope = ({
    groupBy,
    capabilities,
    hasOrganization,
}: TaskGroupingScope & { groupBy: TaskGroupBy }): boolean => {
    switch (groupBy) {
        case TaskGroupBy.CLOSE_STAGE:
        case TaskGroupBy.CLOSE:
            return hasOrganization;
        case TaskGroupBy.TASK_GROUP:
            return capabilities.canGroupByTaskGroup;
        default:
            return isTaskGroupBySupported({ groupBy, capabilities });
    }
};

/**
 * Whether the page offers `groupBy`: in scope, and for stage and service once
 * the organization has stages or closes. The header grouping picker and
 * Board's swimlane options both follow it.
 */
export const isTaskGroupByAvailable = ({
    groupBy,
    hasCloseStages,
    hasCloses,
    ...scope
}: TaskGroupingContext & { groupBy: TaskGroupBy }): boolean => {
    if (!isTaskGroupByInScope({ ...scope, groupBy })) {
        return false;
    }
    switch (groupBy) {
        case TaskGroupBy.CLOSE_STAGE:
            return hasCloseStages;
        case TaskGroupBy.CLOSE:
            return hasCloses;
        default:
            return true;
    }
};
